import { CSSProperties, useState } from 'react';
import { Volume2 } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useBriefAudio } from '../../../lib/use-brief-audio';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { ACTIVITY_MAP_BI_PAGE } from '../../../lib/activity-map-bi';
import { Button, IconButton } from '../ui';
import { BriefListenModal } from './BriefListenModal';

/**
 * Listen — and follow along. Shared by the desktop and mobile brief heroes, which differ only in how the
 * button sits in its row. `iconOnly` renders it as a ghost volume-2 icon button (Figma 13517-97120) beside
 * the primary CTA — the shape every surface now uses (desktop `large`, mobile `medium`, matching native).
 * The outlined text-button branch is the legacy presentation, kept for the `fullWidth` stacked layout.
 *
 * Listening opens the read-along modal rather than just starting audio in the background: a minute of
 * speech with nothing on screen tracking it is the thing users lose their place in. Closing the modal
 * stops the voice — audio still talking behind a dismissed dialog has no visible control to stop it,
 * which is the same bug the unmount cleanup in `useBriefAudio` exists to prevent.
 */
export function BriefListenButton({
  fullWidth,
  iconOnly,
  size = 'medium',
  style,
  className,
  activityMap = false,
}: {
  fullWidth?: boolean;
  iconOnly?: boolean;
  size?: 'medium' | 'large';
  style?: CSSProperties;
  className?: string;
  /**
   * Drawn in the Activity Map's Brief (the canvas popover, the phone map's Brief modal): the press reports the
   * map's `activity-map-brief:listen` with `state` start | stop on its page, and the read-along's own actions
   * take that page too. Elsewhere it keeps the catch-up names.
   */
  activityMap?: boolean;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const audio = useBriefAudio();
  const [open, setOpen] = useState(false);

  const close = () => {
    setOpen(false);
    audio.stop();
  };

  const label = t(audio.state === 'idle' ? 'brief.listen' : 'brief.stopListening');
  const onClick = () => {
    if (activityMap) {
      const state = open ? 'stop' : 'start';
      logPageClickBi(
        { page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-brief:listen', state },
        { additionalFields: { state } },
      );
    } else logPageClickBi({ page_name: 'catch_up', button_name: 'play_brief' });
    if (open) {
      close();
      return;
    }
    setOpen(true);
    audio.toggle();
  };

  return (
    <>
      {iconOnly ? (
        <IconButton
          variant="outlined"
          size={size}
          aria-label={label}
          loading={audio.state === 'loading'}
          style={style}
          className={className}
          onClick={onClick}
        >
          <Volume2 size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
        </IconButton>
      ) : (
        <Button
          size="large"
          variant="outlined"
          fullWidth={fullWidth}
          loading={audio.state === 'loading'}
          style={style}
          className={className}
          onClick={onClick}
        >
          {label}
        </Button>
      )}
      <BriefListenModal open={open} onClose={close} audio={audio} page={activityMap ? ACTIVITY_MAP_BI_PAGE : undefined} />
    </>
  );
}
